import { Injectable } from '@angular/core';
import { Model, ModelFactory, Utilities, } from '@socrates/common';
// I once changed this to import from a directory package, and it made my life miserable trying
// to figure out why it couldn't load them. Still crying a bit right now actually.
import { IRestSearch } from './irestSearch.repo';
import { SearchOptions } from './SearchOptions';
import { SearchResponseInfo } from './SearchResponseInfo';
import { ILocalRecordFilter } from './ILocalRecordFilter';
import { LocalRecordFilter } from './LocalRecordFilter';

// this class tells the cacheManager which data it would like, and is the result the searching
// service gets as a result
export class PageInfo<T extends Model> {
	private _currentPage = 1;                             // for the pagination service
	private _pageIndex = 0;                               // the 0-based index of page we're currently on
	private _pageSize = 25;                               // the number of hits per page (set to default page size)
	private _results: T[] = [];                                   // contains only the current, requested page
	private _searchOptions: SearchOptions = new SearchOptions();  // the current, operating search options object
	private _totalPages = 0;                              // the number of pages in the result set
	private _totalResults = 0;                            // the total number of hits in the current search
	private _searchPerformed = false;

	get currentHit(): number {
		return (this._pageIndex * this._pageSize) + 1;
	}

	get firstIndex(): number {
		return this._pageIndex * this._pageSize;
	}  // returns the real index into the cached results list for the first hit on the current page

	getRecord(desiredId: string): T {
		let desiredRecord = null;
		if (this._results) {
			// eslint-disable-next-line @typescript-eslint/prefer-for-of
			for (let li = 0; li < this._results.length; li++) {
				const record: T = this._results[li];
				if (record.id === desiredId) {
					desiredRecord = record;
					break;
				}
			}
		}
		return desiredRecord;
	}

	resetSearch(): void {
		this._pageIndex = 0;
		this._searchOptions = new SearchOptions();
		this._totalResults = 0;
		this._currentPage = 1;
		this._results = [];
	}

	setSearchOptions(searchOptions: SearchOptions): void {
		this._searchOptions = searchOptions.clone();
		this._pageSize = this._searchOptions.pageSize;
	}

	get toHit(): number {
		const lastHitOnFullPage = this.currentHit + this._pageSize - 1;
		return lastHitOnFullPage > this._totalResults ? this._totalResults : lastHitOnFullPage;
	}

	get currentPage(): number {
		return this._currentPage;
	}

	set currentPage(newCurrentPage: number) {
		this._currentPage = newCurrentPage;
	}

	get pageIndex(): number {
		return this._pageIndex;
	}// the 0-based index of page we're currently on

	set pageIndex(newPageIndex) {
		this._pageIndex = newPageIndex;
	}

	get pageSize(): number {
		return this._pageSize;
	}

	set pageSize(newPageSize) {
		this._pageSize = newPageSize;
	}

	get results(): T[] {
		return this._results;
	}

	set results(newResults: T[]) {
		this._results = newResults;
	}

	get searchOptions(): SearchOptions {
		return this._searchOptions;
	}

	set searchOptions(newSearchOptions: SearchOptions) {
		this._searchOptions = newSearchOptions;
	}

	get totalPages(): number {
		return this._totalPages;
	}

	set totalPages(newTotalPages: number) {
		this._totalPages = newTotalPages;
	}

	get totalResults(): number {
		return this._totalResults;
	}

	set totalResults(newTotalResults: number) {
		this._totalResults = newTotalResults;
	}

	get searchPerformed(): boolean {
		return this._searchPerformed;
	}

	set searchPerformed(newValue: boolean) {
		this._searchPerformed = newValue;
	}
}

export class CacheManager<T extends Model> {

	private _cachedResultsList: T[] = [];               // the entirety of the returned results
	private _locallyFilteredRecords: T[];               // latest filtering hits.. same search & sort can populate next page
	private _currentRecord: T = null;                   // the record that is currently being edited
	private _dataPulled = false;                        // has data been pulled?
	private _disableServerCache = false;                // include the disable cache clause in the query
	private _fixedParameters: any = null;               // a json object that can be extended onto every request
	private _idField = 'id';
	private _lastPull: Date = new Date('2013/01/01');
	private _listRestPath = '';                         // list rest path for searching
	private _numCachedResults = 0;                      // the count of the cachedResultsList
	private _numDesiredPages = 1;                       // number of pages we want the rest server to return
	private _pageSize = 0;                              // the current size of the results pages
	private _retrieving = false;                        // true while the request is left unfulfilled
	private _startPage = 0;                             // page index of starting page (this contains num-desired-pages pages)
	private _totalPages = 0;                            // total pages in the result set (1-based)
	private _totalResults = 0;                          // total number of hits in search
	private _currSearchOptions: SearchOptions = null;
	private _hasAllResults = false;                     // has all results for search
	private _hasAllResultsEmptySearch = false;          // an empty search was performed and all results came back.
	private _timeoutValue: number = 60 * 10 * 1000;     // the number of millis til expiration of cache
	private _allowLocalFiltering: boolean;              // skip the local filtering
	private _resultsModifiedSinceLastSearch: boolean;   // Internal flag to indicate Model added or removed
	// private _resultsHandler: Function;                  // Results handler
	private _resultsHandler: (response: any, pageInfo: PageInfo<T>, searchOptions: SearchOptions) => PageInfo<T>;
	private _localRecordFilter: ILocalRecordFilter;     // Object to handle searching of records locally.. can override

	constructor(private searcher: IRestSearch, private _modelFactory: ModelFactory<T>) {
		this._resultsHandler = this.handleResponse.bind(this);
		this._localRecordFilter = new LocalRecordFilter();
	}

	expireCache() {
		this._lastPull = new Date('2013/01/01');
	}

	findRecord(desiredId, originalRecord = false): T {
		return Utilities.findRecord<T>(this.cachedResultsList, desiredId, this.idField, originalRecord);
	}

	getCachedFirstIndex() {
		return this._startPage * this.pageSize;
	}

	getCachedLastIndex() {
		return this.getCachedFirstIndex() + this.numCachedResults - 1;
	}

	getPageStartIndex(pageNumber) {
		return (pageNumber - this._startPage) * this.pageSize;
	}  // returns the index in the cache that represents the index of the 1st element on the requested page

	getRecord(pageInfo: PageInfo<T>, restPath: string, desiredId: number | string, wantRealRecord: boolean): Promise<T> {
		const deferred = new Promise<T>((resolve, reject) => {

			const recordId: number | string = desiredId;

			if (!this.currentRecord || this.currentRecord.id !== recordId || wantRealRecord) {
				const record = this.findRecord(recordId, true);
				if (Utilities.isValidRecord(record)) {
					this.currentRecord = record;
					if (wantRealRecord) {
						resolve(record);
					} else {
						resolve(this._modelFactory.clone(record));
					}
				} else {

					const handleResponse = (rekord: T) => {
						if (Utilities.isValidRecord(rekord)) {
							rekord = this._modelFactory.massage([rekord])[0];
							this.postRecord(rekord);
							this.currentRecord = rekord;
							if (wantRealRecord) {
								resolve(rekord);
							} else {
								resolve(this._modelFactory.clone(rekord));
							}
						} else {
							reject(rekord);
						}
					};

					if (!this.dataPulled && this._currSearchOptions) {
						// when refresh is hit, want to try reloading the entire result set first
						this.retrieveResults(pageInfo, this._currSearchOptions).then(
							() => {
								const rekord: T = this.findRecord(recordId);
								if (!Utilities.isValidRecord(rekord)) {
									this.getRecord(pageInfo, restPath, recordId, wantRealRecord).then(
										(rec: T) => {
											handleResponse(rec);
										}
									);
								} else {
									this.currentRecord = rekord;
									handleResponse(rekord);
								}
							},
							(rejected) => {
								reject(rejected);
							}
						);
					} else {
						reject('Item not found.');
					}
				}
			} else {
				if (wantRealRecord) {
					resolve(this.currentRecord);
				} else {
					resolve(this._modelFactory.clone(this.currentRecord));
				}
			}
		});
		return deferred;
	}

	hasRequestedPage(requestedPageIndex: number, requestedPageSize: number) {
		let hasPage = false;

		if (this.cachedResultsList && this.cachedResultsList.length > 0) {
			const desiredStartIndex = requestedPageIndex * requestedPageSize;
			const firstCachedIndex = this.getCachedFirstIndex();
			const lastCachedIndex = this.getCachedLastIndex();
			let lastDesiredIndex = desiredStartIndex + requestedPageSize - 1;
			if (this._hasAllResults && lastDesiredIndex >= this.cachedResultsList.length) {
				lastDesiredIndex = this.cachedResultsList.length - 1;
			}
			hasPage = desiredStartIndex >= firstCachedIndex && lastDesiredIndex <= lastCachedIndex;
		}
		return hasPage;
	}

	// TODO: should a pageInfo class be updated here?
	postRecord(record: T, updateOnly = false) {
		this._modelFactory.massage([record]);
		Utilities.postToCache<T>(this.cachedResultsList, record, this.idField, updateOnly);
		this.currentRecord = record;
		this.numCachedResults = this.cachedResultsList.length;
		this._resultsModifiedSinceLastSearch = true;
	}

	postPotentiallyOverridingRecordToCache(record: T): void {
		this._modelFactory.massage([record]);
		Utilities.postPotentiallyOverridingRecordToCache<T>(this.cachedResultsList, record);
		this.currentRecord = record;
		this.numCachedResults = this.cachedResultsList.length;
		this._resultsModifiedSinceLastSearch = true;
	}

	// pulls the required items from the argument array into the results attribute
	populatePageResults(pageInfo: PageInfo<T>, records: T[]) {

		const startIndex = this.getPageStartIndex(pageInfo.pageIndex);
		pageInfo.results = [];
		const endIndex = startIndex + pageInfo.searchOptions.pageSize;

		for (let li = startIndex; li < endIndex && records[li]; li++) {
			const obj: T = this._modelFactory.clone(records[li]);
			pageInfo.results.push(obj);
		}
	}

	removeRecord(pageInfo: PageInfo<T>, record: T): Promise<PageInfo<T>> {

		Utilities.pullFromCache(this.cachedResultsList, record[this.idField], this.idField);
		this.numCachedResults = this.cachedResultsList.length;
		this._resultsModifiedSinceLastSearch = true;
		return this.retrieveResults(pageInfo, this._currSearchOptions);
	}

	removeRecordById(pageInfo: PageInfo<T>, recordId: string): Promise<PageInfo<T>> {

		Utilities.pullFromCache(this.cachedResultsList, recordId, this.idField);
		this.numCachedResults = this.cachedResultsList.length;
		this._resultsModifiedSinceLastSearch = true;
		return this.retrieveResults(pageInfo, this._currSearchOptions);
	}

	resetCurrentRecord(): void {
		if (Utilities.isValidRecord(this.currentRecord)) {
			const record: T = this.findRecord(this.currentRecord.id);
			this.currentRecord = this._modelFactory.clone(record);
		}
	}

	retrieveResults(pageInfo: PageInfo<T>, newSearchOptions: SearchOptions): Promise<PageInfo<T>> {
		const deferred = new Promise<PageInfo<T>>((resolve, reject) => {
			if (!this._retrieving) {

				this._retrieving = true;
				const expired = this.timedOut();
				const sameSearchAndSort: boolean = pageInfo.searchOptions.sameSearchAndSortExpression(newSearchOptions);
				if ((!sameSearchAndSort || this.pageSize !== newSearchOptions.pageSize) && this._dataPulled) {
					pageInfo.pageIndex = 0;
				} else if (pageInfo.pageIndex === 0 && newSearchOptions.currentPage > 1) {
					pageInfo.pageIndex = newSearchOptions.currentPage - 1;
					pageInfo.currentPage = newSearchOptions.currentPage;
				}

				// Ok, 5 options:
				// 	expired, pull again
				// 	same search and sort and page is cached
				// 	have all results from same search expression so can perform different sort
				// 	have all results from empty search so can filter and sort
				// 	need to pull different search result from rest server

				if (!expired && !this._resultsModifiedSinceLastSearch && sameSearchAndSort &&
						this.hasRequestedPage(pageInfo.pageIndex, newSearchOptions.pageSize) && pageInfo.totalResults > 0) {

					try {
						pageInfo.searchOptions = newSearchOptions.clone();
						this.pageSize = pageInfo.pageSize = newSearchOptions.pageSize;
						this.populatePageResults(pageInfo, this._locallyFilteredRecords);
						this.totalPages = pageInfo.totalPages = Math.trunc(pageInfo.totalResults / newSearchOptions.pageSize);
						if (pageInfo.totalResults % newSearchOptions.pageSize) {
							this.totalPages++;
							pageInfo.totalPages++;
						}
						this.totalResults = pageInfo.totalResults;
						this._retrieving = false;
						pageInfo.currentPage = pageInfo.pageIndex + 1;
						resolve(pageInfo);
					}
					catch (e) {
						reject(e);
					}
					finally {
						this._retrieving = false;
					}

				} else if (!expired && this._allowLocalFiltering && this.canLocalSearch(pageInfo.searchOptions, newSearchOptions) && pageInfo.totalResults > 0) {

					this._locallyFilteredRecords = this._cachedResultsList;

					try {
						if (!sameSearchAndSort || this._resultsModifiedSinceLastSearch) {
							this._locallyFilteredRecords = this._localRecordFilter.searchRecords(this._locallyFilteredRecords, newSearchOptions);
						}

						pageInfo.totalResults = this._locallyFilteredRecords.length;
						pageInfo.setSearchOptions(newSearchOptions);
						pageInfo.totalPages = Math.floor(((pageInfo.totalResults + pageInfo.pageSize - 1) / pageInfo.pageSize));
						pageInfo.pageIndex = 0;
						pageInfo.currentPage = 1;
						this.populatePageResults(pageInfo, this._locallyFilteredRecords);
						this._currSearchOptions = newSearchOptions.clone();
						pageInfo.searchPerformed = true;

						resolve(pageInfo);
					}
					catch (e) {
						reject(e);
					}
					finally {
						this._retrieving = false;
					}

				} else {

					try {
						const parameters = newSearchOptions.getSearchOptions();

						if (this.fixedParameters) {
							Utilities.extend(parameters, this.fixedParameters);
						}
						if (this.disableServerCache) {
							Utilities.extend(parameters, { 'MN-NOCACHE': 1 });
						}

						Utilities.extend(parameters, { page_index: pageInfo.pageIndex });
						this.searcher.search(this.listRestPath, parameters, this._modelFactory).then(
							(response: SearchResponseInfo<Model>) => {
								pageInfo = this._resultsHandler(response, pageInfo, newSearchOptions);
								pageInfo.searchPerformed = true;
								this.pageSize = pageInfo.pageSize;
								resolve(pageInfo);
							},
							(errorResponse) => {
								reject('Error occurred: ' + errorResponse.statusText || 'Error returning search results');
								this._retrieving = false;
							}
						);
					}
					catch (e) {
						reject(e);
					}
					finally {
						this._retrieving = false;
					}
				}
				this._resultsModifiedSinceLastSearch = false;
			} else {
				pageInfo.results = this.cachedResultsList;
				resolve(pageInfo);
			}
		});
		return deferred;
	}

	timedOut(): boolean {
		const nowTime: number = new Date().getTime();
		const elapsedTime: number = nowTime - this._lastPull.getTime();

		return (elapsedTime > this._timeoutValue);
	}

	get lastFilteredResults(): T[] {
		return this._locallyFilteredRecords;
	}

	get cachedResultsList(): T[] {
		return this._cachedResultsList;
	}

	set cachedResultsList(newCachedResultsList: T[]) {
		this._cachedResultsList = newCachedResultsList;
	}

	get currentRecord(): T {
		return this._currentRecord;
	}

	set currentRecord(newCurrentRecord: T) {
		this._currentRecord = newCurrentRecord;
	}

	get dataPulled(): boolean {
		return this._dataPulled;
	}

	set dataPulled(newDataPulled: boolean) {
		this._dataPulled = newDataPulled;
	}

	get disableServerCache(): boolean {
		return this._disableServerCache;
	}

	set disableServerCache(newDisableServerCache: boolean) {
		this._disableServerCache = newDisableServerCache;
	}

	get fixedParameters(): any {
		return this._fixedParameters;
	}

	set fixedParameters(newFixedParameters: any) {
		this._fixedParameters = newFixedParameters;
	}

	get idField(): string {
		return this._idField;
	}

	set idField(newIdField: string) {
		this._idField = newIdField;
	}

	get listRestPath(): string {
		return this._listRestPath;
	}

	set listRestPath(newListRestPath: string) {
		this._listRestPath = newListRestPath;
	}

	get numCachedResults(): number {
		return this._numCachedResults;
	}

	set numCachedResults(newNumCachedResults: number) {
		this._numCachedResults = newNumCachedResults;
	}

	get numDesiredPages(): number {
		return this._numDesiredPages;
	}

	set numDesiredPages(newNumDesiredPages: number) {
		this._numDesiredPages = newNumDesiredPages;
	}

	get pageSize(): number {
		return this._pageSize;
	}

	set pageSize(newPageSize: number) {
		this._pageSize = newPageSize;
	}

	get startPage(): number {
		return this._startPage;
	}

	set startPage(newStartPage: number) {
		this._startPage = newStartPage;
	}

	get totalPages(): number {
		return this._totalPages;
	}

	set totalPages(newTotalPages: number) {
		this._totalPages = newTotalPages;
	}

	get totalResults(): number {
		return this._totalResults;
	}

	set totalResults(newTotalResults: number) {
		this._totalResults = newTotalResults;
	}

	get modelFactory(): ModelFactory<T> {
		return this._modelFactory;
	}

	set modelFactory(newModelFactory: ModelFactory<T>) {
		this._modelFactory = newModelFactory;
	}

	get timeoutMinutes(): number {
		return this._timeoutValue / (60 * 1000);
	}

	set timeoutMinutes(newTimeoutMinutes: number) {
		this._timeoutValue = newTimeoutMinutes * 60 * 1000;
	}

	get allowLocalFiltering(): boolean {
		return this._allowLocalFiltering;
	}

	set allowLocalFiltering(localFiltering: boolean) {
		this._allowLocalFiltering = localFiltering;
	}

	private canLocalSearch(oldSearchOptions: SearchOptions, newSearchOptions: SearchOptions): boolean {
		return this._cachedResultsList && this._cachedResultsList.length > 0 && this._hasAllResultsEmptySearch ||
			(this._hasAllResults && oldSearchOptions.sameSearchExpression(newSearchOptions));
	}

	private handleResponse(response: any, pageInfo: PageInfo<T>, newSearchOptions: SearchOptions): PageInfo<T> {
		this.dataPulled = true;
		this._startPage = response.startPage;
		this._locallyFilteredRecords = this.cachedResultsList = response.rows;
		this.numCachedResults = response.rows.length;
		this._lastPull = new Date();
		let pageSize: number = pageInfo.pageSize;
		if (response.pageSize) {
			pageSize = response.pageSize;
		}
		this.pageSize = pageInfo.pageSize = pageSize;
		pageInfo.currentPage = pageInfo.pageIndex + 1;
		let totalPages: number = response.totalPages;
		if (isNaN(response.totalPages)) {
			totalPages = Math.trunc(response.totalResults / pageSize);
			if ((response.totalResults % pageSize) > 0) {
				totalPages++;
			}
		}
		this.totalPages = pageInfo.totalPages = totalPages;
		this.totalResults = pageInfo.totalResults = response.totalResults;
		this._hasAllResults = (response.totalResults === this.numCachedResults);
		if (this._hasAllResults) {
			const searchExpression: string = newSearchOptions.searchExpression;
			this._hasAllResultsEmptySearch = (!searchExpression || searchExpression.length === 0 || searchExpression === '{}');
		} else {
			this._hasAllResultsEmptySearch = false;
		}
		pageInfo.searchOptions = newSearchOptions.clone();
		response.rows = this._modelFactory.massage(response.rows);
		this.populatePageResults(pageInfo, this._cachedResultsList);
		this._currSearchOptions = newSearchOptions.clone();
		return pageInfo;
	}

	set localRecordFilter(newLocalSearcher: ILocalRecordFilter) {
		this._localRecordFilter = newLocalSearcher;
	}
}

export class RestSearch<T extends Model> {

	private _pageInfo: PageInfo<T> = null;
	private _cacheManager: CacheManager<T> = null;

	constructor(private _serviceName: string, searcher: IRestSearch, factory: ModelFactory<T>) {
		this._pageInfo = new PageInfo<T>();
		this._cacheManager = new CacheManager<T>(searcher, factory);
	}

	getRecord(restPath: string, desiredId: number | string, realRecord = false): Promise<T> {
		return this._cacheManager.getRecord(this._pageInfo, restPath, desiredId, realRecord);
	}

	hasResults(): boolean {
		return this._pageInfo.results.length > 0;
	}

	navigate(pageNumber: number): Promise<PageInfo<T>> {
		// check bounds
		if (pageNumber < 1) {
			pageNumber = 1;
		}
		if (pageNumber > this._pageInfo.totalPages) {
			pageNumber = this._pageInfo.totalPages;
		}

		this._pageInfo.pageIndex = pageNumber - 1; // set page index
		this._pageInfo.currentPage = pageNumber;
		this._pageInfo.searchOptions.currentPage = pageNumber;

		return this._cacheManager.retrieveResults(this._pageInfo, this._pageInfo.searchOptions);
	}

	removeRecord(record: T): Promise<PageInfo<T>> {
		return this._cacheManager.removeRecord(this._pageInfo, record);
	}

	removeRecordById(recordId: string): Promise<PageInfo<T>> {
		return this._cacheManager.removeRecordById(this._pageInfo, recordId);
	}

	resetCurrentRecord(): void {
		this._cacheManager.resetCurrentRecord();
	}

	retrieveResults(searchOptions: SearchOptions): Promise<PageInfo<T>> {
		return this._cacheManager.retrieveResults(this._pageInfo, searchOptions);
	}

	setListRestPath(restPath: string): void {
		this._cacheManager.listRestPath = restPath;
	}

	setLocalRecordFilter(recordFilter: ILocalRecordFilter): void {
		this._cacheManager.localRecordFilter = recordFilter;
	}

	disableServerCache(): void {
		this._cacheManager.disableServerCache = true;
	}

	enableServerCache(): void {
		this._cacheManager.disableServerCache = false;
	}

	get serverCacheDisabled(): boolean {
		return this._cacheManager.disableServerCache;
	}

	getSearchOptions(): SearchOptions {
		return this._pageInfo.searchOptions;
	}

	setSearchOptions(searchOptions: SearchOptions): void {
		this._pageInfo.searchOptions = searchOptions;
	}

	resetSearchOptions(): void {
		this._pageInfo.searchOptions.resetSearchOptions();
	}

	postRecord(record: T): void {
		this._cacheManager.postRecord(record);
	}

	postPotentiallyOverridingRecordToCache(record: T): void {
		this._cacheManager.postPotentiallyOverridingRecordToCache(record);
	}

	timedOut(): boolean {
		return this._cacheManager.timedOut();
	}

	get cachedResults(): T[] {
		return this._cacheManager.cachedResultsList;
	}

	get serviceName(): string {
		return this._serviceName;
	}

	get modelFactory(): ModelFactory<T> {
		return this._cacheManager.modelFactory;
	}

	set modelFactory(newFactory: ModelFactory<T>) {
		this._cacheManager.modelFactory = newFactory;
	}

	get timeoutMinutes(): number {
		return this._cacheManager.timeoutMinutes;
	}

	set timeoutMinutes(newTimeoutMinutes: number) {
		this._cacheManager.timeoutMinutes = newTimeoutMinutes;
	}

	get allowLocalFiltering(): boolean {
		return this._cacheManager.allowLocalFiltering;
	}

	set allowLocalFiltering(localFiltering: boolean) {
		this._cacheManager.allowLocalFiltering = localFiltering;
	}

	get idField(): string {
		return this._cacheManager.idField;
	}

	set idField(newIdField: string) {
		this._cacheManager.idField = newIdField;
	}
}


@Injectable()
export class RestSearchService {

	private _cacheArray: any[] = [];

	constructor(private searcher: IRestSearch) { }

	getSearcher<T extends Model>(serviceName: string, modelFactory: ModelFactory<T>): RestSearch<T> {
		let restSearcher = this._cacheArray.find(
			(cached: any) => {
				return cached.serviceName === serviceName;
			}
		);
		if (restSearcher == null) {
			restSearcher = new RestSearch<T>(serviceName, this.searcher, modelFactory);
			this._cacheArray.push(restSearcher);
		}
		return restSearcher;
	}

	removeSearcher(serviceName): void {
		for (let li = 0; li < this._cacheArray.length; li++) {
			if (this._cacheArray[li].serviceName === serviceName) {
				this._cacheArray.splice(li, 1);
				break;
			}
		}
	}

}
